import { useMemo, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import {
  CompanyFieldsFragment,
  EnterpriseRoleEnum,
  useInvitationTokenForEnterpriseAndRoleQuery,
} from '@/graphql/generated';
import { RadioGroup } from '@/components/form/RadioGroup';
import { CopyTextToClipboardButton } from '@/components/generic/CopyTextToClipboardButton';
import StringDropdown from '../../../generic/dropdown/StringDropdown';
import { generatePath } from 'react-router';
import { AppRoutes } from '@/screens/AppRoutes';
import {
  getDateFromISOString,
  getTimeFromISOString,
} from '@/services/DateService';
import { Field, Label } from '@headlessui/react';
import { EnterpriseTreeSelector } from '../company/enterpriseTreeSelector/EnterpriseTreeSelector';
import { DeepPartial } from '@apollo/client/utilities';

const expirationOptions = () => {
  const { t } = useTranslation();

  return [
    { id: '1d', label: t('enum:duration.1d'), duration: 1 },
    { id: '1w', label: t('enum:duration.1w'), duration: 7 },
    { id: '1m', label: t('enum:duration.1m'), duration: 30 },
    { id: 'unlimited', label: t('enum:duration.unlimited'), duration: null },
  ];
};

export const InvitationModal = ({
  enterprise,
}: {
  enterprise: DeepPartial<CompanyFieldsFragment>;
}) => {
  const [selectedRole, setSelectedRole] = useState(
    EnterpriseRoleEnum.Administrator,
  );

  const [selectedOrgUnit, setSelectedOrgUnit] = useState<
    { id: string; name: string }[]
  >([]);

  const [selectedExpiration, setSelectedExpiration] = useState<{
    id: string;
    label: string;
    duration: number | null;
  } | null>(expirationOptions()[0]);
  const { t, i18n } = useTranslation();

  const expirationDate = useMemo(() => {
    if (selectedExpiration?.duration) {
      return new Date(
        Date.now() + selectedExpiration.duration * 24 * 60 * 60 * 1000,
      );
    }
    return null;
  }, [selectedExpiration?.duration]);

  const { data, loading } = useInvitationTokenForEnterpriseAndRoleQuery({
    variables: {
      input: {
        enterpriseId: enterprise.id || '',
        role: selectedRole,
        organizationUnitIds: selectedOrgUnit.map((ou) => ou.id),
        expirationDate: expirationDate?.toISOString(),
      },
    },
    skip: !enterprise.id,
  });

  const expirationMessage = useMemo(() => {
    if (expirationDate) {
      return t('project.users.invitationModal.expirationMessage', {
        date: `${getDateFromISOString(
          expirationDate.toISOString(),
          i18n.language,
        )}  ${getTimeFromISOString(
          expirationDate.toISOString(),
          i18n.language,
        )}`,
      });
    }
  }, [expirationDate, t]);

  return (
    <div className="flex flex-col gap-4">
      <h6>{t('project.users.invitationModal.chooseRole')}</h6>
      <RadioGroup<EnterpriseRoleEnum>
        value={selectedRole}
        onChange={setSelectedRole}
        options={Object.values(EnterpriseRoleEnum).map((role) => ({
          label: t(`enum:enterpriseRole.${role}`),
          value: role,
        }))}
      ></RadioGroup>

      {(enterprise?.organizationalUnits?.length || 0) > 0 && (
        <div className="flex flex-col gap-2">
          <Field>
            <Label>
              <h6>{t('project.users.invitationModal.chooseOrgUnit')}</h6>
            </Label>
            <EnterpriseTreeSelector
              title={enterprise.name}
              selectedOrgUnit={selectedOrgUnit}
              setSelectedOrgUnit={setSelectedOrgUnit}
            />
          </Field>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <Field>
          <Label>
            <h6>{t('project.users.invitationModal.chooseExpiration')}</h6>
          </Label>

          <StringDropdown
            availableItems={expirationOptions()}
            item={selectedExpiration}
            setItem={setSelectedExpiration}
            itemFormatter={(item) => item.label}
          />
          <p className="sami-body-small text-gray-500 mt-2">{expirationMessage}</p>
        </Field>
      </div>
      <CopyTextToClipboardButton
        buttonLabel={t('project.users.invitationModal.copyButton')}
        textToCopy={
          data?.invitationTokenForEnterpriseAndRole.token
            ? `${window.location.origin}${generatePath(AppRoutes.Register, {
                companyId: enterprise.id || '',
                token: data?.invitationTokenForEnterpriseAndRole.token,
              })}`
            : '#'
        }
        className="purple small"
        disabled={loading || !data?.invitationTokenForEnterpriseAndRole.token}
      />
    </div>
  );
};
